import posthog from "posthog-js";

// Track whether PostHog has been initialized
let isInitialized = false;

// PostHog session in which the visitor opened the chat, so the recording
// resumes after a full page load (e.g. coming back from the Pay. payment page)
const CHAT_RECORDING_SESSION_KEY = "posthogChatRecordingSessionId";

/**
 * Initialize PostHog with the provided API key and options
 */
export const initializePostHog = () => {
  if (isInitialized) return;

  if (process.env.REACT_APP_ENVIRONMENT === "production") {
    posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY, {
      api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
      persistence: "localStorage+cookie",
      // Session replay only starts once the chat is opened, see startChatSessionRecording
      disable_session_recording: true,
      loaded: (client) => {
        const chatSessionId = localStorage.getItem(CHAT_RECORDING_SESSION_KEY);
        if (chatSessionId && chatSessionId === client.get_session_id()) {
          client.startSessionRecording(true);
        }
      },
    });

    isInitialized = true;
    return posthog;
  }

  return null;
};

/**
 * Get the PostHog client if initialized, or null if not
 */
export const getPostHogClient = () => {
  if (!isInitialized) {
    // Check if consent was previously given
    const consentStatus = localStorage.getItem("consentGranted");

    // If consent was given previously, initialize PostHog
    if (consentStatus === "true") {
      return initializePostHog();
    }

    return null;
  }

  return posthog;
};

/**
 * Check if consent has been given and PostHog is available
 */
export function hasTracking() {
  return isInitialized || localStorage.getItem("consentGranted") === "true";
}

/**
 * Start session replay for the rest of this PostHog session. Called when the
 * visitor opens the Digital Chat, so only sessions with the chat open are recorded.
 * `true` overrides the project's sampling, linked flag and trigger settings.
 */
export const startChatSessionRecording = () => {
  const client = getPostHogClient();
  if (!client) return;

  if (!client.sessionRecordingStarted()) {
    client.startSessionRecording(true);
  }
  // Read after starting: starting may rotate an expired session id
  localStorage.setItem(CHAT_RECORDING_SESSION_KEY, client.get_session_id());
};

/**
 * Reset PostHog tracking state when consent is withdrawn
 */
export const resetPostHog = () => {
  if (!isInitialized) return;

  // Stop any chat session recording that is running
  posthog.stopSessionRecording();
  localStorage.removeItem(CHAT_RECORDING_SESSION_KEY);

  // Clear any existing PostHog data
  posthog.reset();
  isInitialized = false;
};
